import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialogue } from '../../types/story';

interface DialogueBoxProps {
  dialogue: Dialogue;
  isActive: boolean;
  onComplete: () => void;
  typewriterEffect?: boolean;
}

export const DialogueBox: React.FC<DialogueBoxProps> = ({
  dialogue,
  isActive,
  onComplete,
  typewriterEffect = true
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setDisplayedText('');
      setIsComplete(false);
      return;
    }

    if (!typewriterEffect) {
      setDisplayedText(dialogue.content);
      setIsComplete(true);
      return;
    }

    let index = 0;
    setDisplayedText('');

    const interval = setInterval(() => {
      if (index < dialogue.content.length) {
        setDisplayedText(prev => prev + dialogue.content[index]);
        index++;
      } else {
        clearInterval(interval);
        setIsComplete(true);
      }
    }, 30);

    return () => clearInterval(interval);
  }, [dialogue.content, isActive, typewriterEffect]);

  // 移除自动推进，让用户完全控制对话进度
  // useEffect(() => {
  //   if (isComplete && isActive) {
  //     const timer = setTimeout(() => {
  //       onComplete();
  //     }, 1000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [isComplete, isActive, onComplete]);

  const getSpeakerStyle = () => {
    switch (dialogue.type) {
      case 'narration':
        return 'italic text-gray-600';
      case 'system':
        return 'text-blue-600 font-mono text-sm';
      default:
        return 'text-gray-900';
    }
  };

  const getSpeakerName = () => {
    if (dialogue.type === 'narration') return '旁白';
    if (dialogue.type === 'system') return '系统';
    return dialogue.speaker || '未知';
  };

  return (
    <AnimatePresence mode="wait">
      {isActive && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mb-4"
        >
          <div className={`bg-white rounded-2xl shadow-lg p-6 ${dialogue.type === 'system' ? 'border-2 border-blue-200' : ''}`}>
            {dialogue.type !== 'narration' && (
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-3">
                  {getSpeakerName()[0]}
                </div>
                <h3 className="text-lg font-semibold">{getSpeakerName()}</h3>
              </div>
            )}
            <p className={`text-lg leading-relaxed ${getSpeakerStyle()}`}>
              {displayedText}
              {isActive && !isComplete && (
                <span className="inline-block w-2 h-5 bg-gray-400 animate-pulse ml-1" />
              )}
            </p>
            {isComplete && (
              <div className="mt-2 text-right">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('继续按钮被点击', { dialogueId: dialogue.id });
                    onComplete();
                  }}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium transition"
                >
                  继续 ▶
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};